import React, { useMemo } from 'react';

import CasinoCRMApi from '../../../../../casino/Api/CRM/CasinoCRMApi';
import CasinoUserBonus from '../../../../../casino/Api/Models/CasinoUserBonus';
import TrueFalseIcon from '../../../../../components/common/TrueFalseIcon';
import { formatCurrency } from '../../../../../utils/formatCurrency';
import Loader from '../../../../elements/Loader';
import Search from '../../../../elements/Search';
import Table from '../../../../elements/Table';
import Card from '../../../../elements/common/Card';
import FilterByColumn, { Filter, selectBooleanOptions } from '../../../../elements/common/FilterByColumn';
import SortButton from '../../../../elements/common/SortButton';
import TableLoader from '../../../../elements/common/TableLoader';
import Paginator from '../../../../elements/paginator';
import { formatDateTimeWithTimeZone } from '../../../../helpers/DateFormatter';
import useAutoPaginator from '../../../../hooks/useAutoPaginator';
import {
    CASINO_DEPOSIT_BONUS_SHOW,
    CASINO_FREESPIN_CAMPAIGN_SHOW,
    getAbsoluteCrmUrl,
} from '../../../pages/Helpers/CrmUrls';

import { replaceUrlParams } from 'crm/casino/pages/Helpers/UrlHelper';

interface Props {
    userId: number;
}

enum BonusTypes {
    FREESPIN_CAMPAIGN = 'freespin campaign',
    DEPOSIT_BONUS = 'deposit bonus',
    UNKNOWN = 'unknown',
}

export default function CasinoBonusHistoryTable({ userId }: Props) {
    const { items, isLoading, auto } = useAutoPaginator<CasinoUserBonus>((page, params) => {
        return CasinoCRMApi.userBonuses.getPaginatedByUserId(page, userId, { params });
    });

    const getBonusType = (userBonus: CasinoUserBonus): BonusTypes => {
        if (userBonus.casino_freespin_campaign_id !== null) {
            return BonusTypes.FREESPIN_CAMPAIGN;
        }

        if (userBonus.casino_deposit_bonus_id !== null) {
            return BonusTypes.DEPOSIT_BONUS;
        }

        return BonusTypes.UNKNOWN;
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Bonus details',
                accessor: 'id',
                Cell: ({ row }: { row: { original: CasinoUserBonus } }) => {
                    let url = '';
                    let title = '';

                    if (row.original.casino_deposit_bonus_id !== null) {
                        url = replaceUrlParams(CASINO_DEPOSIT_BONUS_SHOW, { id: row.original.casino_deposit_bonus_id });
                        title = `(${row.original.casino_deposit_bonus_id}) ${
                            row.original.casino_deposit_bonus?.name ?? ''
                        }`;
                    } else if (row.original.casino_freespin_campaign_id !== null) {
                        url = replaceUrlParams(CASINO_FREESPIN_CAMPAIGN_SHOW, {
                            id: row.original.casino_freespin_campaign_id,
                        });
                        title = `(${row.original.casino_freespin_campaign_id}) ${
                            row.original.freespin_campaign?.name ?? ''
                        }`;
                    }

                    return <a href={getAbsoluteCrmUrl(url)}>{title}</a>;
                },
            },
            {
                Header: 'Type',
                Cell: ({ row }: { row: { original: CasinoUserBonus } }) => getBonusType(row.original),
            },
            {
                Header: <SortButton column="stored_balance_bonus" label="Stored balance bonus" {...auto.sort} />,
                accessor: 'stored_balance_bonus',
                Cell: ({ value }: { value: number }) => formatCurrency(value, 2),
            },
            {
                Header: <SortButton column="is_checked" label="Checked" {...auto.sort} />,
                accessor: 'is_checked',
                Cell: ({ value }: { value: boolean }) => <TrueFalseIcon status={value} />,
            },
            {
                Header: <SortButton column="did_user_fill_requirements" label="Filled requirements" {...auto.sort} />,
                accessor: 'did_user_fill_requirements',
                Cell: ({ value }: { value: boolean }) => <TrueFalseIcon status={value} />,
            },
            {
                Header: <SortButton column="is_prize_received" label="Prize received" {...auto.sort} />,
                accessor: 'is_prize_received',
                Cell: ({ value }: { value: boolean }) => <TrueFalseIcon status={value} />,
            },
            {
                Header: <SortButton column="activated_at" {...auto.sort} />,
                accessor: 'activated_at',
                Cell: ({ value }: { value: string }) => formatDateTimeWithTimeZone(value),
            },
            {
                Header: <SortButton column="activated_until" {...auto.sort} />,
                accessor: 'activated_until',
                Cell: ({ value }: { value: string }) => formatDateTimeWithTimeZone(value),
            },
        ],
        []
    );

    const getContent = () => {
        return (
            <Card title="Casino bonuses">
                <Search {...auto.search} />
                <FilterByColumn {...auto.filter}>
                    <Filter column="stored_balance_bonus" label="Stored balance bonus" type="numberRange" />
                    <Filter column="is_checked" label="Checked" type="select" options={selectBooleanOptions} />
                    <Filter
                        column="did_user_fill_requirements"
                        label="Filled requirements"
                        type="select"
                        options={selectBooleanOptions}
                    />
                    <Filter
                        column="is_prize_received"
                        label="Prize received"
                        type="select"
                        options={selectBooleanOptions}
                    />
                    <Filter column="activated_at" label="Activated at" type="dateRange" />
                    <Filter column="activated_until" label="Activated until" type="dateRange" />
                </FilterByColumn>
                <TableLoader isLoading={isLoading}>
                    <Table columns={columns} data={items} additionalClasses="model-list" />
                </TableLoader>
                <Paginator {...auto.paginator} />
            </Card>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
}
